/**
 * Repeat-cycle fill for the "Changing" FQY panel on Investigate RX.
 *
 * A pharmacist enters one cycle of the SIG pattern by hand (e.g. Warfarin
 * "3 TABLETS FOR 2 DAYS AND THEN 2.5 TABLETS FOR 1 DAY REPEAT CYCLE") and this
 * module repeats that pattern until the authorized quantity is consumed.
 *
 * Pure module: no React, no network, no side effects. All arithmetic is done on
 * integers scaled by SCALE so that repeating a 2.5 / 150.5 style decimal pattern
 * dozens of times never accumulates floating point drift.
 */

/** Doses are stored with at most 3 decimal places elsewhere in this helper. */
export const SCALE = 1000;

/** Hard ceiling on the number of rows the fill may leave in the form. */
export const MAX_GENERATED_ROWS = 400;

export const REPEAT_REASONS = {
    NO_ROWS: 'no_rows',
    INVALID_ROWS: 'invalid_rows',
    ZERO_CYCLE: 'zero_cycle',
    NO_AUTHORIZED_QTY: 'no_authorized_qty',
    AUTHORIZED_BELOW_CYCLE: 'authorized_below_cycle',
    ALREADY_COMPLETE: 'already_complete',
    NO_ROOM: 'no_room'
};

const REASON_MESSAGES = {
    [REPEAT_REASONS.NO_ROWS]: 'Enter one cycle of the pattern first.',
    [REPEAT_REASONS.INVALID_ROWS]: 'Every row needs a whole number of days and a numeric qty before the pattern can be repeated.',
    [REPEAT_REASONS.ZERO_CYCLE]: 'The pattern adds up to zero, so there is nothing to repeat.',
    [REPEAT_REASONS.NO_AUTHORIZED_QTY]: 'No authorized quantity is set for this prescription.',
    [REPEAT_REASONS.AUTHORIZED_BELOW_CYCLE]: 'The authorized quantity is smaller than one cycle of this pattern.',
    [REPEAT_REASONS.ALREADY_COMPLETE]: 'The rows already total the authorized quantity.',
    [REPEAT_REASONS.NO_ROOM]: 'No further rows fit before the prescription end date.'
};

export const repeatReasonMessage = (reason) => REASON_MESSAGES[reason] || 'The pattern could not be repeated.';

/**
 * Parse a decimal quantity into a scaled integer.
 * Returns null for empty / non-numeric / non-finite input.
 */
export const toScaled = (value) => {
    if (value === null || value === undefined) return null;
    const raw = typeof value === 'number' ? value : String(value).trim();
    if (raw === '') return null;
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return Math.round(n * SCALE);
};

/** Convert a scaled integer back to a decimal number. */
export const fromScaled = (scaled) => scaled / SCALE;

/**
 * Sum of every dose on a row, scaled. A row's daily quantity is the sum of its
 * dose_rows (OD has one, BID has two, and so on) — this mirrors
 * calculateTotalDoses() in investigationRx.js exactly.
 * Returns null if the row has no doses or any dose is non-numeric.
 */
const rowDoseSumScaled = (row) => {
    const doseRows = Array.isArray(row?.dose_rows) ? row.dose_rows : [];
    if (doseRows.length === 0) return null;
    let sum = 0;
    for (let i = 0; i < doseRows.length; i += 1) {
        const scaled = toScaled(doseRows[i]?.dose);
        if (scaled === null || scaled < 0) return null;
        sum += scaled;
    }
    return sum;
};

/**
 * Days on a row. Must be a whole positive number — calculateTotalDoses() reads
 * days with parseInt, so a fractional value would silently disagree with the
 * validation line.
 */
const rowDaysValue = (row) => {
    const raw = row?.days === null || row?.days === undefined ? '' : String(row.days).trim();
    if (raw === '') return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) return null;
    return n;
};

/**
 * Copy a base-cycle row into a brand new row.
 *
 * Deliberately rebuilds the row from scratch with exactly the fields
 * defaultFrequencyRow() produces (frequency / dose_rows[dose, iv_code,
 * dose_code, send_to_winrx] / days). The database identity fields `id` and
 * `cycle_no` are NOT carried over: those belong to already-saved rows, and a
 * hand-added row never has them. This is what keeps the UPDATE payload
 * structurally identical to hand entry.
 */
export const cloneCycleRow = (row, days) => ({
    frequency: row?.frequency,
    dose_rows: (Array.isArray(row?.dose_rows) ? row.dose_rows : []).map((doseRow) => ({
        dose: String(doseRow?.dose ?? ''),
        iv_code: doseRow?.iv_code ?? '',
        dose_code: doseRow?.dose_code ?? '',
        send_to_winrx: doseRow?.send_to_winrx === true
    })),
    days: String(days)
});

/**
 * Describe the base cycle held in `rows`, or explain why it is unusable.
 */
export const describeBaseCycle = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return { ok: false, reason: REPEAT_REASONS.NO_ROWS };
    }

    const steps = [];
    let cycleTotalScaled = 0;
    let cycleDays = 0;

    for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        const days = rowDaysValue(row);
        const doseSumScaled = rowDoseSumScaled(row);
        if (days === null || doseSumScaled === null) {
            return { ok: false, reason: REPEAT_REASONS.INVALID_ROWS };
        }
        steps.push({ row, days, doseSumScaled, totalScaled: days * doseSumScaled });
        cycleTotalScaled += days * doseSumScaled;
        cycleDays += days;
    }

    if (cycleTotalScaled <= 0) {
        // Guards the fill loop: a zero-total cycle could never consume the
        // remaining quantity and would spin forever.
        return { ok: false, reason: REPEAT_REASONS.ZERO_CYCLE };
    }

    return { ok: true, steps, cycleTotalScaled, cycleDays };
};

/**
 * Repeat the entered pattern until the authorized quantity is consumed.
 *
 * @param {Object}   params
 * @param {Array}    params.rows          Rows currently in the form; these are the base cycle (cycle 1).
 * @param {*}        params.authorizedQty Authorized quantity (decimal, may be a string).
 * @param {?number}  params.maxTotalDays  Optional stop-date cap on TOTAL days across all rows.
 * @param {number}   params.maxRows       Ceiling on the resulting row count.
 *
 * @returns {Object} On success: { ok:true, rows, cyclesFilled, extraDays, totalDays,
 *                   totalDoses, shortfall, stoppedAtEndDate, truncated, remainingDoses }.
 *                   On refusal: { ok:false, reason, message }.
 *
 * Rules:
 *  - Whole cycles are appended while the remaining quantity covers a full cycle.
 *  - Then the base rows are walked in order and appended only for a WHOLE number
 *    of days that fits in what is left (possibly fewer days than the base row).
 *  - Anything still left over is reported as a shortfall and left for the
 *    pharmacist. The authorized quantity is never exceeded.
 */
export const buildRepeatedCycleRows = ({
    rows,
    authorizedQty,
    maxTotalDays = null,
    maxRows = MAX_GENERATED_ROWS
} = {}) => {
    const base = describeBaseCycle(rows);
    if (!base.ok) {
        return { ok: false, reason: base.reason, message: repeatReasonMessage(base.reason) };
    }

    const authorizedScaled = toScaled(authorizedQty);
    if (authorizedScaled === null || authorizedScaled <= 0) {
        return {
            ok: false,
            reason: REPEAT_REASONS.NO_AUTHORIZED_QTY,
            message: repeatReasonMessage(REPEAT_REASONS.NO_AUTHORIZED_QTY)
        };
    }

    const { steps, cycleTotalScaled, cycleDays } = base;

    if (cycleTotalScaled > authorizedScaled) {
        return {
            ok: false,
            reason: REPEAT_REASONS.AUTHORIZED_BELOW_CYCLE,
            message: repeatReasonMessage(REPEAT_REASONS.AUTHORIZED_BELOW_CYCLE)
        };
    }

    const dayCap = Number.isFinite(maxTotalDays) && maxTotalDays !== null ? Math.floor(maxTotalDays) : null;
    if (dayCap !== null && cycleDays > dayCap) {
        return {
            ok: false,
            reason: REPEAT_REASONS.NO_ROOM,
            message: repeatReasonMessage(REPEAT_REASONS.NO_ROOM)
        };
    }

    if (cycleTotalScaled === authorizedScaled) {
        return {
            ok: false,
            reason: REPEAT_REASONS.ALREADY_COMPLETE,
            message: repeatReasonMessage(REPEAT_REASONS.ALREADY_COMPLETE)
        };
    }

    const rowCeiling = Math.max(steps.length, Math.floor(maxRows) || MAX_GENERATED_ROWS);

    // The rows already in the form are cycle 1.
    const filled = rows.map((row) => row);
    let remainingScaled = authorizedScaled - cycleTotalScaled;
    let totalDays = cycleDays;
    let cyclesFilled = 1;
    let truncated = false;
    let stoppedAtEndDate = false;

    // --- whole cycles -----------------------------------------------------
    const wantedExtraCycles = Math.floor(remainingScaled / cycleTotalScaled);
    const cyclesByRowCeiling = Math.floor((rowCeiling - filled.length) / steps.length);
    const cyclesByDayCap = dayCap === null
        ? wantedExtraCycles
        : Math.floor((dayCap - totalDays) / cycleDays);

    const extraCycles = Math.max(0, Math.min(wantedExtraCycles, cyclesByRowCeiling, cyclesByDayCap));
    if (extraCycles < wantedExtraCycles) {
        if (cyclesByDayCap < wantedExtraCycles && cyclesByDayCap <= cyclesByRowCeiling) stoppedAtEndDate = true;
        if (cyclesByRowCeiling < wantedExtraCycles && cyclesByRowCeiling < cyclesByDayCap) truncated = true;
    }

    for (let c = 0; c < extraCycles; c += 1) {
        for (let s = 0; s < steps.length; s += 1) {
            filled.push(cloneCycleRow(steps[s].row, steps[s].days));
        }
    }
    remainingScaled -= extraCycles * cycleTotalScaled;
    totalDays += extraCycles * cycleDays;
    cyclesFilled += extraCycles;

    // --- trailing partial cycle -------------------------------------------
    let extraDays = 0;
    for (let s = 0; s < steps.length; s += 1) {
        if (remainingScaled <= 0) break;
        if (filled.length >= rowCeiling) { truncated = true; break; }

        const { row, days, doseSumScaled } = steps[s];
        let daysThatFit = Math.floor(remainingScaled / doseSumScaled);
        if (daysThatFit > days) daysThatFit = days;

        if (dayCap !== null) {
            const daysLeftBeforeStop = dayCap - totalDays;
            if (daysLeftBeforeStop <= 0) { stoppedAtEndDate = true; break; }
            if (daysThatFit > daysLeftBeforeStop) {
                daysThatFit = daysLeftBeforeStop;
                stoppedAtEndDate = true;
            }
        }

        // A partial DAY is never appended — only whole days of a row count.
        if (daysThatFit < 1) continue;

        filled.push(cloneCycleRow(row, daysThatFit));
        remainingScaled -= daysThatFit * doseSumScaled;
        totalDays += daysThatFit;
        extraDays += daysThatFit;
    }

    if (dayCap !== null && remainingScaled > 0 && totalDays >= dayCap) {
        stoppedAtEndDate = true;
    }

    return {
        ok: true,
        rows: filled,
        cyclesFilled,
        extraDays,
        totalDays,
        totalDoses: fromScaled(authorizedScaled - remainingScaled),
        authorizedQty: fromScaled(authorizedScaled),
        shortfall: fromScaled(remainingScaled),
        remainingDoses: fromScaled(remainingScaled),
        stoppedAtEndDate,
        truncated
    };
};

/** Trim trailing zeros so 150.5 reads as "150.5" and 144 as "144". */
const tidy = (n) => {
    const rounded = Math.round(n * SCALE) / SCALE;
    return String(Number(rounded.toFixed(3)));
};

/** Warning line shown when the fill could not consume the whole authorized quantity. */
export const describeFillWarning = (result) => {
    if (!result?.ok) return '';
    if (result.stoppedAtEndDate) {
        return `Stopped at the prescription end date — ${tidy(result.shortfall)} doses of the authorized quantity remain.`;
    }
    if (result.truncated) {
        return `Stopped at the ${MAX_GENERATED_ROWS} row limit — ${tidy(result.shortfall)} doses of the authorized quantity remain.`;
    }
    if (result.shortfall > 0) {
        return `${tidy(result.shortfall)} doses could not be placed as whole days — adjust the last row if needed.`;
    }
    return '';
};

/**
 * Whole-day span between two YYYY-MM-DD dates, inclusive of both ends.
 * Returns null if either date is missing or unparseable (guard is then skipped).
 */
export const inclusiveDaySpan = (startYmd, stopYmd) => {
    const parse = (value) => {
        if (!value) return null;
        const text = String(value).trim();
        if (text === '' || text === '0000-00-00') return null;
        const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
        if (!match) return null;
        const [, y, m, d] = match;
        const ms = Date.UTC(Number(y), Number(m) - 1, Number(d));
        if (!Number.isFinite(ms)) return null;
        return ms;
    };

    const start = parse(startYmd);
    const stop = parse(stopYmd);
    if (start === null || stop === null) return null;

    const days = Math.floor((stop - start) / 86400000) + 1;
    return days > 0 ? days : null;
};
